import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getBpeList } from "../../api/bpeApi";
import BPeWizard from "./BPeWizard";

/** Lê dicas opcionais nas tags da solicitação: bpe_origem:... e bpe_destino:... (valor = postback URA ou id). */
function parseBpeTags(tags) {
  const out = { origem: "", destino: "" };
  if (!tags || typeof tags !== "string") return out;
  for (const raw of tags.split(/[,\n;]+/)) {
    const p = raw.trim();
    const low = p.toLowerCase();
    if (low.startsWith("bpe_origem:")) out.origem = p.slice(p.indexOf(":") + 1).trim();
    if (low.startsWith("bpe_destino:")) out.destino = p.slice(p.indexOf(":") + 1).trim();
  }
  return out;
}

/**
 * Conteúdo do accordion "Passagens" no painel de detalhes da conversa.
 * Visível apenas para usuários com permissão bpe.view.
 */
const PassagensBPe = ({ selectedConversation }) => {
  const conversationId = selectedConversation?.id ?? selectedConversation?.id_lead;
  const [bps, setBps] = useState(selectedConversation?.bpes ?? selectedConversation?.passagens ?? []);
  const [loadingBps, setLoadingBps] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const { origem: hintOrigem, destino: hintDestino } = useMemo(
    () => parseBpeTags(selectedConversation?.tags),
    [selectedConversation?.tags]
  );
  const bpeInitialDate = selectedConversation?.nextContactAt || null;

  const loadBpes = useCallback(async () => {
    if (!conversationId) return;
    setLoadingBps(true);
    try {
      const list = await getBpeList(conversationId);
      setBps(Array.isArray(list) ? list : []);
    } catch (_) {
      setBps([]);
    } finally {
      setLoadingBps(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (conversationId) {
      loadBpes();
    } else {
      setBps([]);
    }
  }, [conversationId, loadBpes]);

  const tripSummaryText = useMemo(() => {
    if (!Array.isArray(bps) || !bps.length) return "";
    return bps
      .map((bp) => {
        const line = [
          `${bp.origem ?? bp.origem_viagem} → ${bp.destino ?? bp.destino_viagem}`,
          bp.dataviagem ? `Data: ${bp.dataviagem}` : null,
          bp.localizador ? `Localizador: ${bp.localizador}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return line;
      })
      .join("\n");
  }, [bps]);

  const handleCopySummary = async () => {
    if (!tripSummaryText) return;
    try {
      await navigator.clipboard.writeText(tripSummaryText);
    } catch {
      /* ignore */
    }
    if (conversationId) {
      window.dispatchEvent(
        new CustomEvent("xpro-chat-compose", {
          detail: {
            conversationId: Number(conversationId),
            appendText: `Passagens neste protocolo:\n${tripSummaryText}`,
          },
        })
      );
    }
  };

  const handleEmitirBPe = () => setWizardOpen(true);
  const handleWizardSuccess = () => {
    loadBpes();
    setWizardOpen(false);
  };
  const handleWizardClose = () => setWizardOpen(false);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {loadingBps ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : Array.isArray(bps) && bps.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Passagens emitidas nesta conversa
          </Typography>
          {bps.map((bp, index) => (
            <Box
              key={bp.id ?? index}
              sx={{
                py: 1,
                px: 1.5,
                mb: 1,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2">
                {bp.origem ?? bp.origem_viagem} → {bp.destino ?? bp.destino_viagem}
                {bp.dataviagem && ` • ${bp.dataviagem}`}
                {bp.localizador && ` • Loc: ${bp.localizador}`}
              </Typography>
            </Box>
          ))}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopySummary}
            fullWidth
            sx={{ textTransform: "none", mt: 0.5 }}
          >
            Copiar resumo para o chat
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nenhuma passagem emitida nesta conversa.
        </Typography>
      )}
      {(hintOrigem || hintDestino) && (
        <Typography variant="caption" color="text.secondary">
          Dicas das tags: use postbacks em origem/destino no assistente se configurado (bpe_origem / bpe_destino).
        </Typography>
      )}
      <Button
        variant="contained"
        startIcon={<ConfirmationNumberIcon />}
        onClick={handleEmitirBPe}
        fullWidth
        size="medium"
        disabled={!conversationId}
      >
        Emitir BPe
      </Button>
      <BPeWizard
        open={wizardOpen}
        onClose={handleWizardClose}
        conversationId={conversationId}
        onSuccess={handleWizardSuccess}
        initialOrigem={hintOrigem}
        initialDestino={hintDestino}
        initialDataViagem={bpeInitialDate}
      />
    </Box>
  );
};

export default PassagensBPe;
